import { API_STATUS_CODES } from '../constants/api.constants';

/**
 * Format API error into standardized format
 * @param {Error|Object} error - Error object from API call
 * @returns {Object} Formatted error object
 */
export const formatApiError = (error) => {
  // Network error (no response)
  if (!error.response) {
    return {
      message: error.message || 'Network error occurred',
      status: null,
      code: 'NETWORK_ERROR',
      details: error,
      isNetworkError: true,
    };
  }

  // HTTP error with response
  const { status, data } = error.response;

  return {
    message: data?.message || data?.error || getDefaultErrorMessage(status),
    status,
    code: data?.code || `HTTP_${status}`,
    details: data || {},
    isNetworkError: false,
  };
};

/**
 * Get default error message based on status code
 * @param {number} status - HTTP status code
 * @returns {string} Error message
 */
export const getDefaultErrorMessage = (status) => {
  const messages = {
    [API_STATUS_CODES.BAD_REQUEST]: 'Invalid request. Please check your input.',
    [API_STATUS_CODES.UNAUTHORIZED]: 'You are not authorized. Please login again.',
    [API_STATUS_CODES.FORBIDDEN]: 'You do not have permission to access this resource.',
    [API_STATUS_CODES.NOT_FOUND]: 'The requested resource was not found.',
    [API_STATUS_CODES.CONFLICT]: 'A conflict occurred. The resource may already exist.',
    [API_STATUS_CODES.INTERNAL_SERVER_ERROR]: 'Server error occurred. Please try again later.',
    [API_STATUS_CODES.SERVICE_UNAVAILABLE]: 'Service is temporarily unavailable. Please try again later.',
  };

  return messages[status] || 'An unexpected error occurred.';
};

/**
 * Get user-friendly error message
 * @param {Error|Object} error - Error object
 * @returns {string} User-friendly message
 */
export const getErrorMessage = (error) => {
  if (typeof error === 'string') return error;
  
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};

/**
 * Check if error is a network error
 * @param {Error|Object} error - Error object
 * @returns {boolean} True if network error
 */
export const isNetworkError = (error) => {
  return !error.response || error.code === 'NETWORK_ERROR' || error.message === 'Network Error';
};

/**
 * Check if error is an authentication error
 * @param {Error|Object} error - Error object
 * @returns {boolean} True if auth error
 */
export const isAuthError = (error) => {
  return error?.response?.status === API_STATUS_CODES.UNAUTHORIZED;
};

/**
 * Check if error is a validation error
 * @param {Error|Object} error - Error object
 * @returns {boolean} True if validation error
 */
export const isValidationError = (error) => {
  return error?.response?.status === API_STATUS_CODES.BAD_REQUEST;
};

/**
 * Log error to console in development
 * @param {string} context - Error context (e.g., function name)
 * @param {Error|Object} error - Error object
 */
export const logError = (context, error) => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}] Error:`, error);
    
    if (error?.response) {
      console.error('Response data:', error.response.data);
      console.error('Response status:', error.response.status);
    }
  }
};

/**
 * Handle API error with logging and formatting
 * @param {string} context - Error context
 * @param {Error|Object} error - Error object
 * @returns {Object} Formatted error
 */
export const handleApiError = (context, error) => {
  logError(context, error);
  return formatApiError(error);
};

/**
 * Get validation errors from API response
 * @param {Object} error - Error object
 * @returns {Object} Field-specific errors
 */
export const getValidationErrors = (error) => {
  const errors = {};
  
  if (error?.response?.data?.errors) {
    const apiErrors = error.response.data.errors;
    
    // Handle array of errors
    if (Array.isArray(apiErrors)) {
      apiErrors.forEach((err) => {
        if (err.field) {
          errors[err.field] = err.message;
        }
      });
    }
    // Handle object of errors
    else if (typeof apiErrors === 'object') {
      Object.keys(apiErrors).forEach((field) => {
        errors[field] = apiErrors[field];
      });
    }
  }
  
  return errors;
};
